<div class="content-wrapper-parent">
  <div class="content-wrapper">
    <div class="login-container">
      <!-- Loading Overlay -->
      <div class="loading-overlay" *ngIf="isLoggingIn">
        <div class="loading-content">
          <mat-spinner diameter="60" color="primary"></mat-spinner>
          <p class="loading-text">Logging in...</p>
        </div>
      </div>
      
      <!-- Page Title -->
      <div class="login-header-section" *ngIf="!mfaMode && !passwordResetMode && !forgotPasswordMode">
        <div class="login-title-wrapper">
          <h1 class="login-page-title">
            Data Quality Platform
          </h1>
        </div>
      </div>
      
      <!-- Centered Login Panel -->
      <div class="login-panel">
        <div class="login-panel-content">
          <div class="welcome-header">
            <h1>{{ passwordResetMode ? 'Reset Password' : (mfaMode ? 'Two-Factor Authentication' : 'Login') }}</h1>
          </div>
      
      <!-- MFA Verification Section -->
      <div class="mfa-section" *ngIf="mfaMode && !passwordResetMode && !forgotPasswordMode">
        <div class="flex justify-center" style="margin-bottom: 24px;">
          <div style="width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, var(--ps-digital-blue) 0%, var(--primary-dark) 100%); display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0, 112, 210, 0.2);">
            <mat-icon svgIcon="lock-white" style="color: white; font-size: 32px !important; height: 32px !important; width: 32px !important;"></mat-icon>
          </div>
        </div>
        
        <p class="mfa-description">
          Enter the 6-digit verification code sent to your registered email or authentication app to continue.
        </p>
        
        <form [formGroup]="mfaForm" (ngSubmit)="onMfaSubmit()" class="mfa-form">
          <div class="form-field">
            <label for="mfaCode">Verification Code</label>
            <mat-form-field appearance="outline" class="form-field-standard">
              <input matInput 
                     id="mfaCode"
                     formControlName="code" 
                     type="text"
                     placeholder="000000"
                     maxlength="6"
                     autocomplete="one-time-code"
                     inputmode="numeric"
                     pattern="[0-9]*">
            </mat-form-field>
            <div class="error-message" *ngIf="mfaForm.get('code')?.invalid && mfaForm.get('code')?.touched">
              Please enter a valid 6-digit code
            </div>
            <p style="font-size: 12px; color: var(--text-secondary); margin-top: 8px; text-align: center; font-family: 'Red Hat Display', sans-serif;">
              Code expires in 10 minutes
            </p>
          </div>

          <div class="form-actions">
            <button type="submit" 
                    mat-raised-button 
                    color="primary" 
                    class="mfa-verify-button"
                    [disabled]="!mfaForm.valid || isVerifyingMfa">
              <span *ngIf="!isVerifyingMfa">Verify Code</span>
              <span *ngIf="isVerifyingMfa" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                <mat-spinner diameter="20" style="display: inline-block;"></mat-spinner>
                Verifying...
              </span>
            </button>
            
            <button type="button" 
                    mat-button 
                    (click)="cancelMfa()"
                    class="cancel-button">
              Back to Login
            </button>
          </div>
        </form>
      </div>
      
      <!-- Password Reset Section -->
      <div class="password-reset-section" *ngIf="passwordResetMode && !forgotPasswordMode">
        <div *ngIf="isValidatingCode" class="validation-spinner">
          <p>Validating reset code...</p>
        </div>
        
        <div *ngIf="!isValidatingCode && !resetCodeValid" class="validation-error">
          <p>Invalid or expired reset code.</p>
          <button mat-raised-button 
                  color="primary" 
                  (click)="cancelPasswordReset()">
            Back to Login
          </button>
        </div>
        
        <form *ngIf="!isValidatingCode && resetCodeValid" 
              [formGroup]="passwordResetForm" 
              (ngSubmit)="onPasswordResetSubmit()" 
              class="password-reset-form">
          <p class="reset-description">
            Enter your new password for <strong>{{ resetEmail }}</strong>
          </p>
          
          <div class="form-field">
            <label for="newPassword">New Password</label>
            <mat-form-field appearance="outline" class="form-field-standard">
              <input matInput 
                     id="newPassword"
                     formControlName="newPassword" 
                     [type]="hideNewPassword ? 'password' : 'text'"
                     placeholder="Enter new password"
                     autocomplete="new-password">
              <button mat-icon-button 
                      matSuffix 
                      type="button"
                      (click)="toggleNewPasswordVisibility()"
                      class="password-toggle">
                <mat-icon [svgIcon]="hideNewPassword ? 'visibility_off' : 'visibility'"></mat-icon>
              </button>
            </mat-form-field>
            <div class="error-message" *ngIf="passwordResetForm.get('newPassword')?.invalid && passwordResetForm.get('newPassword')?.touched">
              <span *ngIf="passwordResetForm.get('newPassword')?.errors?.['required']">Password is required</span>
              <span *ngIf="passwordResetForm.get('newPassword')?.errors?.['minlength']">Password must be at least 8 characters</span>
              <span *ngIf="passwordResetForm.get('newPassword')?.errors?.['passwordComplexity']">Password must contain at least one letter and one number</span>
            </div>
          </div>

          <div class="form-field">
            <label for="confirmPassword">Confirm Password</label>
            <mat-form-field appearance="outline" class="form-field-standard">
              <input matInput 
                     id="confirmPassword"
                     formControlName="confirmPassword" 
                     [type]="hideConfirmPassword ? 'password' : 'text'"
                     placeholder="Confirm new password"
                     autocomplete="new-password">
              <button mat-icon-button 
                      matSuffix 
                      type="button"
                      (click)="toggleConfirmPasswordVisibility()"
                      class="password-toggle">
                <mat-icon [svgIcon]="hideConfirmPassword ? 'visibility_off' : 'visibility'"></mat-icon>
              </button>
            </mat-form-field>
          </div>

          <div class="form-actions">
            <button type="submit" 
                    mat-raised-button 
                    color="primary" 
                    class="reset-password-button"
                    [disabled]="!passwordResetForm.valid || isResettingPassword">
              {{isResettingPassword ? 'Resetting...' : 'Reset Password'}}
            </button>
            
            <button type="button" 
                    mat-button 
                    (click)="cancelPasswordReset()"
                    class="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <!-- Login Form -->
      <form *ngIf="!passwordResetMode && !forgotPasswordMode && !mfaMode" 
            [formGroup]="loginForm" 
            (ngSubmit)="onSubmit()" 
            class="login-form">
        <div class="form-field">
          <label for="username">Username</label>
          <mat-form-field appearance="outline" class="form-field-standard">
            <input matInput 
                   id="username"
                   formControlName="username" 
                   type="email"
                   placeholder="Enter your email"
                   autocomplete="username">
          </mat-form-field>
        </div>

        <div class="form-field">
          <label for="password">Password</label>
          <mat-form-field appearance="outline" class="form-field-standard">
            <input matInput 
                   id="password"
                   formControlName="password" 
                   [type]="hidePassword ? 'password' : 'text'"
                   placeholder="Enter your password"
                   autocomplete="current-password">
            <button mat-icon-button 
                    matSuffix 
                    type="button"
                    (click)="togglePasswordVisibility()"
                    class="password-toggle">
              <mat-icon [svgIcon]="hidePassword ? 'visibility_off' : 'visibility'"></mat-icon>
            </button>
          </mat-form-field>
          
          <div class="forgot-password">
            <a href="javascript:void(0)" 
               (click)="showForgotPassword()" 
               class="forgot-link">
              Forgot password?
            </a>
          </div>
        </div>

        <button type="submit" 
                mat-raised-button 
                color="primary" 
                class="login-button"
                [disabled]="!loginForm.valid || isLoggingIn">
          {{isLoggingIn ? 'Logging in...' : 'Login'}}
        </button>

        
      </form>

      <!-- Forgot Password Section -->
      <div class="forgot-password-section" *ngIf="forgotPasswordMode && !passwordResetMode">
        <div class="forgot-header">
          <button mat-icon-button 
                  (click)="cancelForgotPassword()" 
                  class="back-button">
            <mat-icon svgIcon="arrow_back"></mat-icon>
          </button>
          <h2>Reset Password</h2>
        </div>
        <p class="forgot-description">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        <form [formGroup]="forgotPasswordForm" (ngSubmit)="onForgotPasswordSubmit()" class="forgot-password-form">
          <div class="form-field">
            <label for="forgotEmail">Email Address</label>
            <mat-form-field appearance="outline" class="form-field-standard">
              <input matInput 
                     id="forgotEmail"
                     formControlName="email" 
                     type="email"
                     placeholder="Enter your email"
                     autocomplete="email">
            </mat-form-field>
            <div class="error-message" *ngIf="forgotPasswordForm.get('email')?.invalid && forgotPasswordForm.get('email')?.touched">
              Please enter a valid email address
            </div>
          </div>

          <button type="submit" 
                  mat-raised-button 
                  color="primary" 
                  class="forgot-password-button"
                  [disabled]="!forgotPasswordForm.valid || isSendingResetEmail">
            {{isSendingResetEmail ? 'Sending...' : 'Send Reset Link'}}
          </button>
        </form>
      </div>
        </div>
      </div>
    </div>
  </div>
</div>
